import {
  ref,
  watch,
  type ComputedRef,
} from 'vue'
import type { SectionMenuItem } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/SectionMenu/types'
import { ITEM_SPY_OFFSET } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/SectionMenu/composables/useSectionMenuScroll/constants'
import type { ScrollSpyOptions } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/SectionMenu/composables/useSectionMenuScroll/types'

const SPY_LOCK_MS = 800

/** 1px-линия на spyOffset → активный пункт меню */
export default function createSectionMenuScrollSpy(
  items: ComputedRef<SectionMenuItem[]>,
  { spyOffset }: ScrollSpyOptions,
) {
  const prevTabIndex = ref<number>()
  const activeTabIndex = ref<number>()

  let spyLockUntil = 0
  let sectionByEl = new Map<HTMLElement, number>()
  let visibleEls = new Set<HTMLElement>()
  /** top из IntersectionObserverEntry — без getBoundingClientRect */
  let rectTopByEl = new Map<HTMLElement, number>()
  let sectionObserver: IntersectionObserver | null = null
  let appliedOffset = Number.NaN
  let appliedInnerHeight = 0

  function setActive(index: number | undefined) {
    if (activeTabIndex.value === index) {
      return
    }

    prevTabIndex.value = activeTabIndex.value
    activeTabIndex.value = index
  }

  function syncHash() {
    if (!window.location.hash) {
      return
    }

    const index = items.value.findIndex(item => (item.href === window.location.hash))

    setActive(index >= 0 ? index : undefined)
  }

  function pickActive() {
    if (!visibleEls.size) {
      setActive(undefined)

      return
    }

    // На стыке секций берём нижнюю по странице (больший top)
    let next: number | undefined
    let maxTop = Number.NEGATIVE_INFINITY

    visibleEls.forEach(el => {
      const top = rectTopByEl.get(el)

      if (top == null) {
        return
      }

      if (top >= maxTop) {
        maxTop = top
        next = sectionByEl.get(el)
      }
    })

    setActive(next)
  }

  function onSectionIntersect(entries: IntersectionObserverEntry[]) {
    entries.forEach(({ target, isIntersecting, boundingClientRect }) => {
      if (!(target instanceof HTMLElement)) {
        return
      }

      if (isIntersecting) {
        visibleEls.add(target)
        rectTopByEl.set(target, boundingClientRect.top)
      } else {
        visibleEls.delete(target)
        rectTopByEl.delete(target)
      }
    })

    if (Date.now() >= spyLockUntil) {
      pickActive()
    }
  }

  function setup() {
    const offset = spyOffset.value

    if (
      sectionObserver
      && offset === appliedOffset
      && window.innerHeight === appliedInnerHeight
    ) {
      return
    }

    sectionObserver?.disconnect()
    visibleEls = new Set()
    rectTopByEl = new Map()
    sectionByEl = new Map()

    items.value.forEach((item, index) => {
      if (!item.href?.startsWith('#')) {
        return
      }

      const el = document.querySelector<HTMLElement>(item.href)

      if (el) {
        sectionByEl.set(el, index)
      }
    })

    if (!sectionByEl.size) {
      appliedOffset = offset
      appliedInnerHeight = window.innerHeight
      setActive(undefined)

      return
    }

    const bottomMargin = -(window.innerHeight - offset - 1 - ITEM_SPY_OFFSET)

    sectionObserver = new IntersectionObserver(onSectionIntersect, {
      rootMargin: `-${offset}px 0px ${bottomMargin}px 0px`,
      threshold: 0,
    })
    sectionByEl.forEach((_index, el) => sectionObserver?.observe(el))

    appliedOffset = offset
    appliedInnerHeight = window.innerHeight
  }

  function teardown() {
    sectionObserver?.disconnect()
    sectionObserver = null
    appliedOffset = Number.NaN
    appliedInnerHeight = 0
  }

  function lock() {
    spyLockUntil = Date.now() + SPY_LOCK_MS
  }

  function unlockAndPick() {
    spyLockUntil = 0
    pickActive()
  }

  watch(spyOffset, setup)

  return {
    prevTabIndex,
    activeTabIndex,
    setup,
    teardown,
    lock,
    unlockAndPick,
    syncHash,
  }
}
